// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import { palmFacing } from './palmReach'

/** Pure presentation ownership. No menu command, tracking SDK or simulation lives here. */
export interface PalmSessionPoint { readonly x: number; readonly y: number; readonly z: number }

export interface PalmSessionInput {
  readonly tracked: boolean
  readonly headKnown: boolean
  readonly head: PalmSessionPoint
  readonly palm: PalmSessionPoint
  readonly normal: PalmSessionPoint
  readonly headForward: PalmSessionPoint
  readonly openHand: boolean
  readonly atControl: boolean
}

// Product tuning, not claimed headset calibration. Preserve the existing 250 ms intentional
// dwell and 0.70 m reach (palmReach.ts), while distinguishing a raised hand from a low one.
export const PALM_SESSION_DWELL_MS = 250
export const PALM_SESSION_MIN_HEIGHT = -0.28
export const PALM_SESSION_MAX_HEIGHT = 0.18
export const PALM_SESSION_MIN_REACH = 0.15
export const PALM_SESSION_MAX_REACH = 0.7
export const PALM_SESSION_FACING_COS = 0.75
// How square the palm must be to the face (palm normal against head forward). 0.5 (60°), not
// the facing cosine (1.0.215): a palm turned to the eyes but held a natural 0.3 m beside the nose
// sits 40-50° off the line of sight, and the 1.0.214 headset log refused exactly that five times.
export const PALM_SESSION_LOOK_COS = 0.5
// Closing by lowering (1.0.216): 0.12 m below the highest palm AND 0.32 m below the eyes, for
// 100 ms (was 0.15 m and 0.42 m — the 1.0.215 headset log lowered an open palm to 0.35-0.42 m
// below the eyes with the menu still up). See PalmSession.cs.
export const PALM_SESSION_LOWER_HEIGHT = -0.32
export const PALM_SESSION_LOWER_TRAVEL = 0.12
export const PALM_SESSION_LOWER_MS = 100
// Closing by turning the palm away (1.0.216): its normal more than 75° from the eyes for 200 ms.
// An unknown normal is not turned away.
export const PALM_SESSION_TURN_AWAY_COS = 0.25
export const PALM_SESSION_TURN_AWAY_MS = 200
export const PALM_SESSION_LOSS_GRACE_MS = 250
export const PALM_SESSION_REACQUIRE_MS = 80
export const PALM_SESSION_MAX_SAMPLE_MS = 50

function finitePoint(p: PalmSessionPoint): boolean {
  return Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)
}

/** The palm's normal against the line from the palm to the eyes; undefined when unknown. */
export function palmSessionFacing(i: PalmSessionInput): number | undefined {
  if (!finitePoint(i.normal) || !finitePoint(i.palm) || !finitePoint(i.head)) return undefined
  const dx = i.head.x - i.palm.x, dy = i.head.y - i.palm.y, dz = i.head.z - i.palm.z
  const reach = Math.hypot(dx, dy, dz), n = Math.hypot(i.normal.x, i.normal.y, i.normal.z)
  if (reach < 1e-6 || n < 1e-6) return undefined
  return (dx * i.normal.x + dy * i.normal.y + dz * i.normal.z) / (reach * n)
}

/** More than 75° from the eyes. An unknown normal is not turned away. */
export function palmTurnedAway(i: PalmSessionInput): boolean {
  const facing = palmSessionFacing(i)
  return facing !== undefined && facing < PALM_SESSION_TURN_AWAY_COS
}

export class PalmSession {
  private openNow = false
  private interactiveNow = false
  private openedNow = false
  private loweredNow = false
  private lostNow = false
  private hadPose = false
  private unavailable = false
  private recovering = false
  private dwellMs = 0
  private lowerMs = 0
  private turnMs = 0
  private dismissedByTurningNow = false
  private missingMs = 0
  private recoverMs = 0
  private highestPalmY = 0

  get isOpen(): boolean { return this.openNow }
  get interactive(): boolean { return this.interactiveNow }
  get openedEdge(): boolean { return this.openedNow }
  /** A deliberate dismissal closes hand-owned Quick: lowering, or (1.0.216) the palm held turned away. */
  get loweredEdge(): boolean { return this.loweredNow }
  /** The last loweredEdge came from turning the palm away rather than lowering it. */
  get dismissedByTurning(): boolean { return this.dismissedByTurningNow }
  /** The host must release pointer contact and disable Quick input on this edge. */
  get lostEdge(): boolean { return this.lostNow }

  advance(input: PalmSessionInput, frameMs: number): void {
    this.openedNow = this.loweredNow = this.lostNow = false
    const elapsed = Number.isFinite(frameMs) ? Math.max(0, frameMs) : 0
    // A stall cannot stand in for several fresh samples; loss expiry still uses wall duration.
    const sample = Math.min(elapsed, PALM_SESSION_MAX_SAMPLE_MS)
    const valid = input.tracked && input.headKnown && finitePoint(input.head) && finitePoint(input.palm)
    if (!valid) {
      if (!this.unavailable && this.hadPose) this.lostNow = true
      this.unavailable = true
      this.interactiveNow = false
      this.dwellMs = this.lowerMs = this.turnMs = this.recoverMs = 0
      if (this.openNow) {
        this.missingMs += elapsed
        if (this.missingMs >= PALM_SESSION_LOSS_GRACE_MS) this.openNow = false
      }
      return
    }

    const reacquired = this.unavailable
    this.unavailable = false
    this.hadPose = true
    this.missingMs = 0
    if (!this.openNow) {
      this.recovering = false
      this.interactiveNow = false
      if (!this.canEnter(input)) { this.dwellMs = 0; return }
      this.dwellMs += sample
      if (this.dwellMs >= PALM_SESSION_DWELL_MS) {
        this.openNow = this.interactiveNow = this.openedNow = true
        this.highestPalmY = input.palm.y
        this.lowerMs = this.turnMs = 0
      }
      return
    }

    if (reacquired) { this.recovering = true; this.recoverMs = 0 }
    this.highestPalmY = Math.max(this.highestPalmY, input.palm.y)
    // Require actual hand descent AND a low hand relative to the CURRENT eyes. Head nods
    // alone cannot close; translating the whole body leaves the relative-height clause false.
    // Tracking the highest palm also permits standing up and subsequently lowering the hand.
    const lowered = this.highestPalmY - input.palm.y >= PALM_SESSION_LOWER_TRAVEL &&
      input.palm.y - input.head.y <= PALM_SESSION_LOWER_HEIGHT
    if (this.recovering) {
      if (lowered) {
        this.openNow = this.interactiveNow = false
        this.dwellMs = this.lowerMs = this.turnMs = 0
        this.recovering = false
        return // Recovered low is cancellation, never an intentional LoweredEdge.
      }
      this.recoverMs += sample
      this.interactiveNow = false
      if (this.recoverMs < PALM_SESSION_REACQUIRE_MS) return
      this.recovering = false
    }
    if (lowered) {
      this.interactiveNow = false
      this.turnMs = 0
      this.lowerMs += sample
      if (this.lowerMs >= PALM_SESSION_LOWER_MS) {
        this.openNow = false
        this.loweredNow = true
        this.dismissedByTurningNow = false
        this.dwellMs = this.lowerMs = 0
      }
    } else if (palmTurnedAway(input)) {
      this.interactiveNow = false
      this.lowerMs = 0
      this.turnMs += sample
      if (this.turnMs >= PALM_SESSION_TURN_AWAY_MS) {
        this.openNow = false
        this.loweredNow = true
        this.dismissedByTurningNow = true
        this.dwellMs = this.turnMs = 0
      }
    } else {
      this.lowerMs = this.turnMs = 0
      this.interactiveNow = true
    }
  }

  reset(): void {
    this.openNow = this.interactiveNow = this.openedNow = this.loweredNow = this.lostNow = false
    this.hadPose = this.unavailable = this.recovering = false
    this.dwellMs = this.lowerMs = this.turnMs = this.missingMs = this.recoverMs = this.highestPalmY = 0
    this.dismissedByTurningNow = false
  }

  private canEnter(i: PalmSessionInput): boolean {
    if (!i.openHand || i.atControl || !finitePoint(i.normal) || !finitePoint(i.headForward)) return false
    const height = i.palm.y - i.head.y
    if (height < PALM_SESSION_MIN_HEIGHT || height > PALM_SESSION_MAX_HEIGHT) return false
    const dx = i.head.x - i.palm.x, dy = i.head.y - i.palm.y, dz = i.head.z - i.palm.z
    const reach = Math.sqrt(dx * dx + dy * dy + dz * dz)
    if (reach < PALM_SESSION_MIN_REACH || reach > PALM_SESSION_MAX_REACH) return false
    const n = Math.sqrt(i.normal.x * i.normal.x + i.normal.y * i.normal.y + i.normal.z * i.normal.z)
    const f = Math.sqrt(i.headForward.x * i.headForward.x + i.headForward.y * i.headForward.y + i.headForward.z * i.headForward.z)
    if (n < 1e-6 || f < 1e-6) return false
    const facing = palmFacing(i.normal, i.palm, i.head) ?? -1
    const parallel = -(i.normal.x * i.headForward.x + i.normal.y * i.headForward.y + i.normal.z * i.headForward.z) / (n * f)
    return facing >= PALM_SESSION_FACING_COS && parallel >= PALM_SESSION_LOOK_COS
  }
}
